import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import SearchBox from '@/components/feature/SearchBox';

const navLinks = [
  { to: '/', label: '首页' },
  { to: '/about', label: '关于' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-8 h-16 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-book-open-line text-xl"></i>
          </span>
          <span className="font-heading font-extrabold text-2xl text-foreground-950 tracking-tight">韩漫</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `px-3 py-2 rounded-md text-sm font-label font-medium transition-colors cursor-pointer ${
                isActive ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
              }`
            }
          >
            首页
          </NavLink>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setCategoryOpen((v) => !v)}
              className="px-3 py-2 rounded-md text-sm font-label font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-1"
            >
              分类
              <i className={`ri-arrow-down-s-line text-base transition-transform ${categoryOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full mt-2 w-[420px] p-3 bg-background-50 border border-background-200 rounded-xl shadow-lg grid grid-cols-2 gap-1 animate-fade-up">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    <span className="text-sm text-foreground-800 font-label">{cat.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {navLinks.slice(1).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-label font-medium transition-colors cursor-pointer ${
                  isActive ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block flex-1 max-w-md ml-auto">
          <SearchBox />
        </div>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 cursor-pointer"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-up">
          <SearchBox className="w-full" />
          <nav className="mt-4 flex flex-col gap-1">
            <Link to="/" className="px-3 py-3 rounded-lg text-foreground-800 font-label hover:bg-background-100 cursor-pointer">
              首页
            </Link>
            <div className="px-3 pt-2 pb-1 text-xs text-foreground-500 font-label">漫画分类</div>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-background-100 text-foreground-800 cursor-pointer"
                >
                  <i className={`${cat.icon} text-primary-500`}></i>
                  <span className="text-sm font-label">{cat.name}</span>
                </Link>
              ))}
            </div>
            <Link to="/about" className="px-3 py-3 mt-1 rounded-lg text-foreground-800 font-label hover:bg-background-100 cursor-pointer">
              关于我们
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}